import { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  Activity, Radio, Crosshair, Compass, Building2, Scale, Users, Pickaxe, Banknote,
  Search, Menu, Grid3x3, HelpCircle, Settings, X,
} from "lucide-react";

const links = [
  { to: "/gold", label: "Dashboard", icon: Activity },
  { to: "/gold/gold-hunter", label: "Gold Hunter", icon: Pickaxe },
  { to: "/gold/treasury", label: "Treasury", icon: Banknote },
  { to: "/gold/control-room", label: "Control Room", icon: Radio },
  { to: "/gold/strike-deck", label: "Strike Deck", icon: Crosshair },
  { to: "/gold/rover", label: "Rover", icon: Compass },
  { to: "/gold/capital-colony", label: "Capital Colony", icon: Building2 },
  { to: "/gold/legal-colony", label: "Legal Colony", icon: Scale },
  { to: "/gold/partner-view", label: "Partner View", icon: Users },
];

export default function Nav() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const triggerRef = useRef(null);
  const drawerCloseRef = useRef(null);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    document.addEventListener("keydown", onKey);
    drawerCloseRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen && triggerRef.current) triggerRef.current.focus();
  }, [drawerOpen]);

  return (
    <>
      {/* ── Top app bar (Google Workspace style) ── */}
      <header
        data-testid="main-nav"
        className="sticky top-0 z-40"
        style={{
          background: "var(--yb-surface-1)",
          borderBottom: "1px solid var(--yb-border-soft)",
        }}
      >
        <div className="max-w-7xl mx-auto h-16 px-3 sm:px-4 flex items-center gap-2">
          {/* Hamburger */}
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open main menu"
            aria-expanded={drawerOpen}
            aria-controls="main-drawer"
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-black/5"
          >
            <Menu size={22} color="var(--yb-text-secondary)" />
          </button>

          {/* Brand */}
          <NavLink to="/gold" end className="flex items-center gap-2.5 pr-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
              style={{
                background: "linear-gradient(135deg, #f9ab00 0%, #ea8600 100%)",
                color: "#fff",
                boxShadow: "var(--yb-shadow-1)",
              }}
            >
              Y
            </div>
            <span
              className="text-[22px] hidden sm:inline"
              style={{
                fontFamily: "'Google Sans', Roboto, sans-serif",
                color: "var(--yb-text-primary)",
                fontWeight: 400,
              }}
            >
              YABAI
            </span>
          </NavLink>

          {/* Search bar (Google-style pill) */}
          <div className="flex-1 max-w-2xl mx-2 hidden md:block">
            <label htmlFor="global-search" className="sr-only">Search YABAI</label>
            <div
              className="flex items-center h-12 px-4 rounded-full transition-shadow hover:shadow-md focus-within:shadow-md focus-within:bg-white"
              style={{
                background: "var(--yb-surface-2)",
                border: "1px solid transparent",
              }}
            >
              <Search size={20} color="var(--yb-text-secondary)" aria-hidden="true" />
              <input
                id="global-search"
                type="search"
                aria-label="Search colonies, leads and agents"
                placeholder="Search colonies, leads, agents…"
                className="flex-1 bg-transparent outline-none px-4 text-[15px]"
                style={{ color: "var(--yb-text-primary)" }}
              />
            </div>
          </div>

          {/* Right side actions */}
          <div className="ml-auto flex items-center gap-1">
            <NavLink
              to="/"
              className="px-3 h-9 rounded-full flex items-center text-[13px] font-medium transition-colors hover:bg-black/5"
              style={{ color: "#1a73e8", fontFamily: "'Google Sans', Roboto, sans-serif" }}
              title="Back to YABBAI Orchestra"
            >
              Orchestra ↗
            </NavLink>
            <IconButton label="Help"><HelpCircle size={20} color="var(--yb-text-secondary)" /></IconButton>
            <IconButton label="Settings"><Settings size={20} color="var(--yb-text-secondary)" /></IconButton>
            <IconButton label="Apps"><Grid3x3 size={20} color="var(--yb-text-secondary)" /></IconButton>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ml-1"
              style={{ background: "#1a73e8", color: "#fff" }}
              aria-label="Account"
            >
              Y
            </div>
          </div>
        </div>

        {/* ── Tab strip (the navigation links, Material tabs style) ── */}
        <div
          className="max-w-7xl mx-auto px-3 sm:px-4 flex items-center overflow-x-auto"
          style={{ borderTop: "1px solid var(--yb-border-soft)" }}
        >
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/gold"}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative flex items-center gap-2 px-4 h-12 text-[14px] whitespace-nowrap transition-colors ${
                  isActive
                    ? "font-medium"
                    : "hover:bg-black/5"
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? "#1a73e8" : "var(--yb-text-secondary)",
                fontFamily: "'Google Sans', Roboto, sans-serif",
              })}
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} strokeWidth={2} />
                  {label}
                  {isActive && (
                    <span
                      className="absolute left-0 right-0 bottom-0 h-[3px] rounded-t-full"
                      style={{ background: "#1a73e8" }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </header>

      {/* ── Drawer (Google Workspace side nav) ── */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-50 animate-slide-up"
            style={{ background: "rgba(32,33,36,0.45)" }}
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            id="main-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Main navigation"
            className="fixed top-0 left-0 bottom-0 z-50 w-[300px] py-3 animate-slide-up"
            style={{
              background: "var(--yb-surface-1)",
              boxShadow: "var(--yb-shadow-3)",
            }}
          >
            <div className="flex items-center gap-2 px-4 h-12">
              <button
                ref={drawerCloseRef}
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5"
              >
                <X size={22} color="var(--yb-text-secondary)" />
              </button>
              <span
                className="text-[20px]"
                style={{
                  fontFamily: "'Google Sans', Roboto, sans-serif",
                  color: "var(--yb-text-primary)",
                }}
              >
                YABAI
              </span>
            </div>
            <nav className="mt-2 px-2">
              {links.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/gold"}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-4 h-12 px-4 my-0.5 text-[14px] transition-colors ${
                      isActive ? "font-medium" : "hover:bg-black/5"
                    }`
                  }
                  style={({ isActive }) => ({
                    color: isActive ? "#1a73e8" : "var(--yb-text-primary)",
                    background: isActive ? "rgba(26,115,232,0.10)" : "transparent",
                    borderRadius: "0 9999px 9999px 0",
                    marginRight: 8,
                    fontFamily: "'Google Sans', Roboto, sans-serif",
                  })}
                >
                  <Icon size={20} strokeWidth={2} />
                  {label}
                </NavLink>
              ))}
              <NavLink
                to="/"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-4 h-12 px-4 my-0.5 text-[14px] transition-colors hover:bg-black/5"
                style={{
                  color: "#1a73e8",
                  borderRadius: "0 9999px 9999px 0",
                  marginRight: 8,
                  fontFamily: "'Google Sans', Roboto, sans-serif",
                }}
              >
                <Grid3x3 size={20} strokeWidth={2} />
                YABBAI Orchestra ↗
              </NavLink>
            </nav>
          </aside>
        </>
      )}
    </>
  );
}

function IconButton({ children, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-black/5"
    >
      {children}
    </button>
  );
}
